import React, { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Loader2, AlertCircle } from "lucide-react";

/**
 * NpsnVerifier — reusable NPSN input + verification button block for the
 * admin school forms. Persists verification via /admin/schools/verify-npsn
 * when `schoolId` is provided (edit flow), otherwise only surfaces the
 * lookup result so the create form can copy values into its own state.
 *
 * Props:
 *   value: string             current NPSN input
 *   onChange: (npsn) => void  standard controlled input
 *   schoolId?: string         if present, verification is persisted immediately
 *   verified?: boolean        current government_verified flag (edit form only)
 *   onLookup?: (result) => void  called with the raw lookup payload so parents
 *                                can pre-fill address/coords in "new" flow
 *   testidPrefix?: string
 */
export default function NpsnVerifier({
  value,
  onChange,
  schoolId,
  verified,
  onLookup,
  testidPrefix = "npsn",
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { verified, error, kemdikbud }

  const verify = async () => {
    const npsn = (value || "").trim();
    if (!npsn) {
      toast.error("Isi NPSN dulu (8 digit angka)");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const body = schoolId ? { npsn, school_id: schoolId } : { npsn };
      const r = await api.post("/admin/schools/verify-npsn", body);
      setResult(r.data);
      if (r.data.verified) {
        toast.success("NPSN terverifikasi di database Kemdikbud");
        onLookup?.(r.data);
      } else {
        toast.error(r.data.error || "NPSN tidak terverifikasi");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghubungi server");
    } finally {
      setBusy(false);
    }
  };

  const isVerified = result?.verified ?? verified;
  const errorMsg = result?.error;
  const k = result?.kemdikbud;

  return (
    <div className="rounded-xl border border-[#DBEAFE] bg-[#EFF6FF] p-3 space-y-2" data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-semibold flex items-center gap-1.5">
          <ShieldCheck size={12} strokeWidth={2} />
          NPSN — Verifikasi Data Kemdikbud
        </div>
        {isVerified && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-white bg-[#065F46] rounded-full px-2 py-0.5"
            data-testid={`${testidPrefix}-verified-badge`}
          >
            <ShieldCheck size={10} strokeWidth={2.5} /> Terverifikasi
          </span>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          placeholder="mis: 20104201 (8 digit)"
          value={value || ""}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 8))}
          maxLength={8}
          inputMode="numeric"
          data-testid={`${testidPrefix}-input`}
          className="rounded-xl border-[#DBEAFE] flex-1"
        />
        <Button
          type="button"
          onClick={verify}
          disabled={busy || !(value || "").trim()}
          data-testid={`${testidPrefix}-verify-btn`}
          className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10 px-4"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Verifikasi"}
        </Button>
      </div>
      {k && (
        <div className="text-[11px] text-[#1E3A8A] bg-white/60 border border-[#DBEAFE] rounded-lg p-2 space-y-0.5" data-testid={`${testidPrefix}-lookup-preview`}>
          <div><span className="text-[#4B5563]">Nama resmi:</span> <span className="font-semibold">{k.sekolah}</span></div>
          <div><span className="text-[#4B5563]">Alamat:</span> {k.alamat_jalan}, {k.kecamatan}, {k.kabupaten_kota}, {k.propinsi}</div>
          <div><span className="text-[#4B5563]">Bentuk:</span> {k.bentuk} · <span className="text-[#4B5563]">Status:</span> {k.status === "N" ? "Negeri" : k.status === "S" ? "Swasta" : k.status}</div>
        </div>
      )}
      {errorMsg && (
        <div className="text-[11px] text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-2 flex items-start gap-1.5" data-testid={`${testidPrefix}-error`}>
          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}
      <div className="text-[10px] text-[#4B5563]">
        NPSN diverifikasi ke referensi Kemdikbud. Jika ditemukan, badge <span className="font-semibold text-[#065F46]">"Terverifikasi Data Pemerintah"</span> akan muncul di profil sekolah.
      </div>
    </div>
  );
}
